import { all } from 'redux-saga/effects';
import { Product } from '@portal/portal-core';

import { configureSuperAdminSagas as configureSuperAdminServerSagas } from 'ducks/super-client';
import { combinedAuthClientSagas, AuthClientUtils } from 'ducks/auth-client';
import configureAuthorizedDevicesSagas from 'sagas/authorized-devices';
import configureCustomFieldsSagas from 'ducks/custom-fields/sagas';
import { configureAgreementSagas } from 'ducks/agreements-client';
import { combinedAdminClientSagas } from 'ducks/admin-client';
import { combinedUserClientSagas } from 'ducks/user-client';
import configureUserAccountSagas from 'sagas/user-account';
import configureSuperAdminSagas from 'sagas/super-admin';
import configureGDPRSagas from 'sagas/gdpr/gdpr';
import { serverUrl } from '../Config';

const { getAuthToken } = AuthClientUtils;

const AuthClientSagas = combinedAuthClientSagas(
  // `clientStorePath` is read by the underlying sagas at runtime but is not
  // declared on the `SignInConfig` param type; cast to that param type.
  {
    baseApiUrl: serverUrl,
    productName: Product.NAMES.cloud,
    clientStorePath: 'auth.',
  } as Parameters<typeof combinedAuthClientSagas>[0],
);

const AgreementSagas = configureAgreementSagas(
  // `getAuthToken` returns `string | null`, but the config param types it as
  // `(key?: string) => string`; a null token is handled downstream at runtime,
  // so cast to that param type.
  {
    baseApiUrl: serverUrl,
    getAuthToken,
  } as Parameters<typeof configureAgreementSagas>[0],
);

const AuthorizedDevicesSagas = configureAuthorizedDevicesSagas({
  baseApiUrl: serverUrl,
  getAuthToken,
});

const GDPRSagas = configureGDPRSagas(
  // `getAuthToken` is required at runtime by the shared saga boundary but is
  // not declared on the GDPR saga's minimal config type; cast to that param type.
  { baseApiUrl: serverUrl, getAuthToken } as Parameters<typeof configureGDPRSagas>[0],
);

const UserSagas = combinedUserClientSagas(
  // `getAuthToken` returns `string | null`, but the config param types it as
  // `() => string | Promise<string>`; a null token is handled downstream at
  // runtime, so cast to that param type.
  {
    baseApiUrl: serverUrl,
    getAuthToken,
    clientStorePath: 'userClient.userInvites.',
  } as Parameters<typeof combinedUserClientSagas>[0],
);

const UserAccountSagas = configureUserAccountSagas({
  baseApiUrl: serverUrl,
  getAuthToken,
});

const AdminSagas = combinedAdminClientSagas({
  baseApiUrl: serverUrl,
  getAuthToken,
  clientStorePath: 'adminClient.adminInvites.',
});

const SuperAdminServerSagas = configureSuperAdminServerSagas(
  // `db` is required by the root saga config type but is not supplied at runtime
  // here (watchers that use it are unreachable in this app); cast to that param type.
  {
    baseApiUrl: serverUrl,
    getAuthToken,
    timeout: 11000,
  } as Parameters<typeof configureSuperAdminServerSagas>[0],
);

const SuperAdminSagas = configureSuperAdminSagas({
  baseApiUrl: serverUrl,
  getAuthToken,
  timeout: 11000,
});

const CustomFieldSagas = configureCustomFieldsSagas({
  baseApiUrl: serverUrl,
  getAuthToken,
});

/* istanbul ignore next reason: no rootSaga tests */
export default function* rootSaga(): Generator<unknown, void, unknown> {
  yield all([
    AuthClientSagas(),
    AgreementSagas(),
    UserSagas(),
    UserAccountSagas(),
    AdminSagas(),
    SuperAdminServerSagas(),
    SuperAdminSagas(),
    AuthorizedDevicesSagas(),
    GDPRSagas(),
    CustomFieldSagas(),
  ]);
}
